import { useState } from "react";

import { prettifyApiError } from "../../shared/api";
import {
  inviaBozza,
  scartaBozza,
  SEGNAPOSTO,
  urlAllegato,
  type Bozza,
  type TipoBozza,
} from "./api";

/** Davide 03/11/2026: «prima bozze, poi da sole». OM prepara la mail per
 *  Bracchi; qui Davide la legge, la corregge se serve e preme Invia. Il
 *  tasto è l'unico punto da cui parte una mail a Bracchi. */

const TITOLI: Record<TipoBozza, string> = {
  format: "Format pronto da mandare",
  risposta: "Risposta da completare",
  sollecito: "Sollecito pronto",
};

const BOTTONE =
  "inline-flex h-[32px] items-center justify-center rounded-full border px-3.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11";

const giornoOra = (iso: string) =>
  new Date(iso).toLocaleString("it-IT", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

export function BozzaBracchi({ bozza, onCambio }: { bozza: Bozza; onCambio: () => void }) {
  const [aperta, setAperta] = useState(false);
  const [testo, setTesto] = useState(bozza.testo);
  const [lavoro, setLavoro] = useState<"invio" | "scarto" | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  if (bozza.stato === "inviata") {
    return (
      <p className="mt-2 text-[12.5px] text-ink-2">
        Mail mandata a Bracchi{bozza.inviata_il ? ` il ${giornoOra(bozza.inviata_il)}` : ""}.
      </p>
    );
  }
  if (bozza.stato === "in_invio" || bozza.stato === "errore") {
    return (
      <p role="alert" className="mt-2 text-[12.5px] font-semibold text-red">
        {bozza.stato === "in_invio"
          ? "Invio interrotto: controlla in Posta inviata se la mail è partita."
          : `Mail non partita: ${bozza.errore ?? "errore sconosciuto"}.`}
      </p>
    );
  }

  const daCompletare = testo.includes(SEGNAPOSTO);

  async function esegui(azione: "invio" | "scarto") {
    setLavoro(azione);
    setErrore(null);
    try {
      if (azione === "invio") await inviaBozza(bozza.id, testo);
      else await scartaBozza(bozza.id);
      onCambio();
    } catch (err) {
      setErrore(prettifyApiError(err));
    } finally {
      setLavoro(null);
    }
  }

  return (
    <div className="mt-2.5 rounded-lg border border-line-2 bg-paper/60 px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] font-bold text-ink">{TITOLI[bozza.tipo]}</p>
        <button
          type="button"
          onClick={() => setAperta((v) => !v)}
          aria-expanded={aperta}
          className={`${BOTTONE} border-line-2 bg-card text-ink hover:bg-paper-2`}
        >
          {aperta ? "Chiudi" : "Leggi la mail"}
        </button>
      </div>

      {aperta && (
        <div className="mt-2.5 space-y-2">
          <p className="text-[12.5px] text-ink-2">
            A <span className="font-semibold text-ink">{bozza.destinatario}</span> · {bozza.oggetto}
          </p>
          <label className="block">
            <span className="sr-only">Testo della mail</span>
            <textarea
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
              rows={8}
              className="w-full rounded-lg border border-line-2 bg-card px-3 py-2 text-[13.5px] leading-snug text-ink outline-none focus:border-ink"
            />
          </label>
          {bozza.allegato && (
            <p className="text-[12.5px] text-ink-2">
              Allegato:{" "}
              <a
                href={urlAllegato(bozza.id)}
                download={bozza.allegato}
                className="font-semibold text-ink underline"
              >
                {bozza.allegato}
              </a>
            </p>
          )}
          {daCompletare && (
            <p className="text-[12.5px] font-semibold text-amber">
              Scrivi la risposta al posto di {SEGNAPOSTO}.
            </p>
          )}
          {errore && (
            <p role="alert" className="text-[12.5px] font-semibold text-red">
              {errore}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={lavoro !== null || daCompletare || !testo.trim()}
              onClick={() => void esegui("invio")}
              className={`${BOTTONE} border-ink bg-ink text-paper hover:opacity-90`}
            >
              {lavoro === "invio" ? "Invio…" : "Invia a Bracchi"}
            </button>
            <button
              type="button"
              disabled={lavoro !== null}
              onClick={() => void esegui("scarto")}
              className={`${BOTTONE} border-line-2 bg-card text-ink hover:bg-paper-2`}
            >
              Scarta
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
